/**
 * AutomationSocketService
 *
 * Manages Socket.IO connection to the /automation namespace for real-time
 * automation execution logs and pending decision events.
 *
 * Uses socket.io-client directly (not AFFiNE's SocketConnection) because
 * the backend AutomationGateway runs on a separate namespace with its own
 * subscription protocol.
 */

import { notify } from '@affine/component';
import type { WorkflowPatchEvent } from '@streamlain/workflow-field-schemas';
import { LiveData, Service } from '@toeverything/infra';
import type { Socket } from 'socket.io-client';

import {
  markBackendAvailable,
  onBackendAvailable,
} from '../../../utils/backend-availability';
import {
  createServerSocketConnection,
  ensureSocketServerConfig,
} from '../../../utils/socket';
import type { WorkspaceServerService } from '../../cloud';
import type { DocsService } from '../../doc';
import type { PendingDecisionService } from '../../notification/services/pending-decision';
import type { PendingDecision } from '../../notification/stores/pending-decision';
import type { WorkspaceService } from '../../workspace';
import {
  type PendingPatchRegistry,
  routeWorkflowPatchEvent,
} from '../workflow-patch';
import type { ExecutionService } from './execution';
import type { LoggingService } from './logging';

/** Event payload from backend AutomationGateway */
interface ExecutionLogEvent {
  workspaceId: string;
  blockId: string;
  logId: string;
  docId: string;
  mode: 'execute' | 'trigger';
  status: string;
  stage: 'input' | 'process' | 'output';
  stageData: unknown;
  timestamp: number;
}

/** Event payload for execution status updates (completion/failure) */
interface ExecutionUpdateEvent {
  workspaceId: string;
  blockId: string;
  logId: string;
  docId: string;
  status: 'completed' | 'failed' | 'cancelled';
  completedAt?: number;
  error?: string;
}

interface ExecutionProgressEvent {
  workspaceId: string;
  blockId: string;
  logId: string;
  docId: string;
  mode: 'execute' | 'trigger';
  executionId: string;
  stepId: string;
  stepName: string;
  stepType: string;
  phase: 'start' | 'progress' | 'complete';
  activity?: string;
  message?: string;
  percent?: number;
  currentTimeMs?: number;
  totalTimeMs?: number;
  timeRemainingMs?: number;
  bytesDone?: number;
  bytesTotal?: number;
  bitrate?: string;
  fps?: number;
  detail?: Record<string, string | number | boolean>;
  timestamp: number;
}

/** Event payload for pending decision events */
interface PendingDecisionEvent {
  type: 'created' | 'resolved';
  decision: PendingDecision;
  timestamp: number;
}

/** sl-czam: Event payload for workflow schema change notification */
interface WorkflowSchemaChangedEvent {
  workflowId: string;
  timestamp: number;
}

export interface WorkflowSnapshotEvent {
  kind: 'created' | 'updated';
  workspaceId: string;
  workflowId: string;
  snapshotId: string;
  version: number;
  role:
    | 'draft'
    | 'published'
    | 'manual'
    | 'ai-pending'
    | 'ai-accepted'
    | 'ai-rejected';
  name: string;
  active: boolean;
  publishedAt: string | null;
  changedAt: string;
}

export interface WorkflowWorkingCopyEvent {
  workspaceId: string;
  workflowId: string;
  commandType: string;
  actorId?: string | null;
  changedAt: string;
}

/** sl-wqvr.7.6: Event payload for workflow provisioning completion */
interface WorkflowProvisioningCompleteEvent {
  workspaceId: string;
  engineUrl: string;
  adminEmail: string;
  adminPassword: string;
  timestamp: number;
}

export class AutomationSocketService extends Service {
  readonly workflowSnapshot$ = new LiveData<WorkflowSnapshotEvent | null>(null);
  readonly workflowWorkingCopy$ = new LiveData<WorkflowWorkingCopyEvent | null>(
    null
  );

  private socket: Socket | null = null;
  private connected = false;
  private connecting: Promise<void> | null = null;
  private recoveryConnectTimer: ReturnType<typeof setTimeout> | null = null;
  private backendAvailabilityUnsubscribe: (() => void) | null = null;
  private disposed = false;
  private pendingDecisionService: PendingDecisionService | null = null;
  private readonly lastProgressEventAt = new Map<string, number>();
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly loggingService: LoggingService,
    private readonly executionService: ExecutionService,
    private readonly docsService: DocsService,
    /** sl-9fmv.6.5: live-apply patch sink, co-scoped in WorkspaceScope. */
    private readonly pendingPatchRegistry: PendingPatchRegistry
  ) {
    super();

    // Auto-connect when service is instantiated
    this.connect().catch(error => {
      console.error('[AutomationSocket] Auto-connect failed:', error);
    });
  }

  /**
   * Set the PendingDecisionService for real-time updates.
   * Called from notification module when available.
   */
  setPendingDecisionService(service: PendingDecisionService): void {
    this.pendingDecisionService = service;
  }

  getCurrentSocketId(): string | null {
    return this.socket?.connected ? (this.socket.id ?? null) : null;
  }

  /**
   * Connect to Socket.IO /automation namespace and subscribe to workspace events.
   */
  async connect(): Promise<void> {
    if (this.disposed) return;
    if (this.connected && this.socket?.connected) return;
    if (this.connecting) return this.connecting;

    this.connecting = this.doConnect();
    try {
      await this.connecting;
    } finally {
      this.connecting = null;
    }
  }

  private async doConnect(): Promise<void> {
    this.startBackendAvailabilityListener();

    const server = this.workspaceServerService.server;
    if (!server) {
      console.warn(
        '[AutomationSocket] No server available, skipping socket connection'
      );
      this.scheduleRecoveryConnect();
      return;
    }

    try {
      await ensureSocketServerConfig(server);
      if (this.socket) {
        this.teardownSocket();
      }
      // Connect to /automation namespace (NOT the main doc-sync namespace).
      // Keep this aligned with WorkspaceRealtimeService recovery semantics:
      // rebuild a stale namespace socket and resubscribe after each reconnect.
      this.socket = createServerSocketConnection(server, '/automation');
      this.setupSocketListeners(this.socket);

      await this.waitForSocketConnect(this.socket);
      const subscribed = await this.subscribeWorkspace();
      if (!subscribed) {
        this.teardownSocket();
        this.scheduleRecoveryConnect();
        return;
      }

      this.connected = true;
      markBackendAvailable();
    } catch (error) {
      console.error('[AutomationSocket] Connection failed:', error);
      this.connected = false;
      this.teardownSocket();
      this.scheduleRecoveryConnect();
    }
  }

  /**
   * Disconnect from Socket.IO.
   */
  disconnect(): void {
    this.disposed = true;
    if (this.recoveryConnectTimer) {
      clearTimeout(this.recoveryConnectTimer);
      this.recoveryConnectTimer = null;
    }
    this.backendAvailabilityUnsubscribe?.();
    this.backendAvailabilityUnsubscribe = null;
    this.teardownSocket();
    this.connected = false;
  }

  private teardownSocket(): void {
    if (this.socket) {
      try {
        const workspaceId = this.workspaceService.workspace.id;

        // Unsubscribe from workspace
        if (this.socket.connected) {
          this.socket.emit('automation:unsubscribe', { workspaceId });
        }

        // Remove listeners
        this.socket.off('connect', this.handleSocketConnected);
        this.socket.off('connect_error', this.handleSocketConnectError);
        this.socket.off('disconnect', this.handleSocketDisconnected);
        this.socket.off('automation:execution-log', this.handleExecutionLog);
        this.socket.off(
          'automation:execution-progress',
          this.handleExecutionProgress
        );
        this.socket.off(
          'automation:execution-update',
          this.handleExecutionUpdate
        );
        this.socket.off(
          'automation:pending-decision',
          this.handlePendingDecision
        );
        this.socket.off(
          'automation:provisioning-complete',
          this.handleProvisioningComplete
        );
        this.socket.off(
          'automation:workflow-schema-changed',
          this.handleSchemaChanged
        );
        this.socket.off('automation:workflow-patch', this.handleWorkflowPatch);
        this.socket.off(
          'automation:workflow-snapshot',
          this.handleWorkflowSnapshot
        );
        this.socket.off(
          'automation:workflow-working-copy',
          this.handleWorkflowWorkingCopy
        );

        // Disconnect
        this.socket.disconnect();
      } catch (error) {
        console.warn('[AutomationSocket] Error during disconnect:', error);
      }
      // sl-3nkm Phase 4b audit F3: Flush throttle map on disconnect
      // so a fresh connection does not inherit keys from the previous
      // session.
      this.lastProgressEventAt.clear();
      this.socket = null;
    }
    this.connected = false;
  }

  private setupSocketListeners(socket: Socket): void {
    socket.on('connect', this.handleSocketConnected);
    socket.on('connect_error', this.handleSocketConnectError);
    socket.on('disconnect', this.handleSocketDisconnected);
    socket.on('automation:execution-log', this.handleExecutionLog);
    socket.on('automation:execution-progress', this.handleExecutionProgress);
    socket.on('automation:execution-update', this.handleExecutionUpdate);
    socket.on('automation:pending-decision', this.handlePendingDecision);
    socket.on(
      'automation:provisioning-complete',
      this.handleProvisioningComplete
    );
    socket.on('automation:workflow-schema-changed', this.handleSchemaChanged);
    socket.on('automation:workflow-patch', this.handleWorkflowPatch);
    socket.on('automation:workflow-snapshot', this.handleWorkflowSnapshot);
    socket.on(
      'automation:workflow-working-copy',
      this.handleWorkflowWorkingCopy
    );
  }

  private waitForSocketConnect(socket: Socket): Promise<void> {
    if (socket.connected) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Connection timeout'));
      }, 10000);
      const cleanup = () => {
        clearTimeout(timeout);
        socket.off('connect', handleConnect);
        socket.off('connect_error', handleConnectError);
      };
      const handleConnect = () => {
        cleanup();
        resolve();
      };
      const handleConnectError = (error: Error) => {
        cleanup();
        reject(error);
      };
      socket.once('connect', handleConnect);
      socket.once('connect_error', handleConnectError);
    });
  }

  private async subscribeWorkspace(): Promise<boolean> {
    if (!this.socket?.connected) return false;
    const workspaceId = this.workspaceService.workspace.id;
    const subscribeResult = await this.socket.emitWithAck(
      'automation:subscribe',
      { workspaceId }
    );

    if (!subscribeResult?.success) {
      console.error(
        '[AutomationSocket] Failed to subscribe to workspace:',
        subscribeResult
      );
      return false;
    }
    return true;
  }

  private startBackendAvailabilityListener(): void {
    if (this.backendAvailabilityUnsubscribe) return;
    this.backendAvailabilityUnsubscribe = onBackendAvailable(() => {
      this.scheduleRecoveryConnect();
    });
  }

  private scheduleRecoveryConnect(): void {
    if (
      this.disposed ||
      (this.connected && this.socket?.connected) ||
      this.recoveryConnectTimer
    ) {
      return;
    }
    this.recoveryConnectTimer = setTimeout(() => {
      this.recoveryConnectTimer = null;
      if (this.disposed || (this.connected && this.socket?.connected)) return;
      void this.connect();
    }, 1000);
  }

  private readonly handleSocketConnected = (): void => {
    if (this.connecting) return;
    void this.subscribeWorkspace()
      .then(subscribed => {
        this.connected = subscribed;
        if (subscribed) {
          markBackendAvailable();
          return;
        }
        this.teardownSocket();
        this.scheduleRecoveryConnect();
      })
      .catch(error => {
        console.error('[AutomationSocket] Re-subscribe failed:', error);
        this.connected = false;
        this.teardownSocket();
        this.scheduleRecoveryConnect();
      });
  };

  private readonly handleSocketDisconnected = (): void => {
    this.connected = false;
    this.scheduleRecoveryConnect();
  };

  private readonly handleSocketConnectError = (error: Error): void => {
    console.warn('[AutomationSocket] Connect error:', error);
    this.connected = false;
    this.scheduleRecoveryConnect();
  };

  /**
   * Handle incoming execution log event from backend.
   * Triggers a refresh of logs from the API since socket events are partial updates.
   */
  private readonly handleExecutionLog = (event: ExecutionLogEvent): void => {
    console.debug(
      '[AutomationSocket] Received execution log event:',
      event.logId,
      event.stage
    );

    this.loggingService.applySocketStage(
      event.blockId,
      event.logId,
      event.mode,
      event.stage,
      event.stageData as {
        status?: string;
        startedAt?: string | Date;
        completedAt?: string | Date;
        data?: Record<string, unknown>;
        error?: string;
      },
      event.timestamp
    );

    // Keep backend refresh as authoritative follow-up persistence sync.
    this.loggingService.refreshLogs(event.blockId, event.workspaceId);
  };

  /**
   * Handle incoming execution update event from backend (completion/failure).
   * Updates ExecutionService state, clears activeLog, and refreshes logs.
   */
  private readonly handleExecutionUpdate = (
    event: ExecutionUpdateEvent
  ): void => {
    console.debug(
      '[AutomationSocket] Received execution update event:',
      event.logId,
      event.status
    );

    // sl-3nkm Phase 4b audit F2: drop events from a stale run. If the
    // frontend has started a newer run on the same block, the active
    // logId has already advanced; a late terminal event from the
    // previous run would otherwise flip the newer run's state to
    // completed/cancelled and corrupt its timeout anchor.
    const activeLog = this.loggingService.getActiveLog(event.blockId);
    if (activeLog && activeLog.id !== event.logId) {
      console.debug(
        `[AutomationSocket] Dropping stale execution update: event.logId=${event.logId} activeLogId=${activeLog.id}`
      );
      return;
    }

    // Update ExecutionService state (this triggers UI update)
    this.executionService.setStatusFromBackend(
      event.blockId,
      event.status,
      event.error
    );

    // sl-3nkm Phase 4b audit F3: Evict this log's throttle keys from
    // the progress map on terminal status. Without this the map grows
    // unbounded across the workspace session — one key per row, and
    // after the switch to executionId-granularity (round-4) even
    // single-step batches contribute one key per row.
    for (const key of this.lastProgressEventAt.keys()) {
      if (key.startsWith(`${event.logId}:`)) {
        this.lastProgressEventAt.delete(key);
      }
    }

    // Clear the activeLog since execution is complete
    // This prevents the stale "RUNNING" status from showing in HistoryPanel
    this.loggingService.completeLog(event.blockId, event.status, event.error);

    // Also refresh logs from backend API to get the complete log
    this.loggingService.refreshLogs(event.blockId, event.workspaceId);

    // Persist lastRunAt/lastRunStatus to Y.js block (single source of truth)
    this.updateBlockRunStatus(event.docId, event.blockId, event.status);

    // Failure toasts are surfaced through NotificationSocketService: the
    // backend emits an AutomationFailed `notification:created` event,
    // which the notification socket renders as a toast. Duplicating the
    // toast here caused the user to see the same failure three times
    // (notification-socket + automation-socket + execution-panel).
    // Keeping the state update + log refresh above is still necessary.
  };

  private readonly handleExecutionProgress = (
    event: ExecutionProgressEvent
  ): void => {
    // sl-3nkm Phase 4b audit F2: drop progress events from a stale
    // run. Same rationale as handleExecutionUpdate — a late progress
    // frame from an older log would re-arm the timeout anchor under
    // the wrong executionId in the newer run.
    const activeLog = this.loggingService.getActiveLog(event.blockId);
    if (activeLog && activeLog.id !== event.logId) {
      return;
    }

    // sl-3nkm.8.4: throttle per-row, not per-step. Batch execution
    // reuses `stepId` across rows; a `logId:stepId` key would drop
    // the first event of a subsequent row if it lands within 1s of
    // the previous row's last event, confusing downstream state.
    const throttleKey = `${event.logId}:${event.executionId}`;
    const lastAt = this.lastProgressEventAt.get(throttleKey) ?? 0;
    const shouldForce =
      event.phase === 'complete' || (event.percent ?? 0) >= 100;
    if (!shouldForce && event.timestamp - lastAt < 1000) {
      return;
    }
    this.lastProgressEventAt.set(throttleKey, event.timestamp);

    this.loggingService.applyExecutionProgress(event.blockId, {
      logId: event.logId,
      mode: event.mode,
      timestamp: event.timestamp,
      stepId: event.stepId,
      stepName: event.stepName,
      stepType: event.stepType,
      executionId: event.executionId,
      phase: event.phase,
      activity: event.activity,
      message: event.message,
      percent: event.percent,
      currentTimeMs: event.currentTimeMs,
      totalTimeMs: event.totalTimeMs,
      timeRemainingMs: event.timeRemainingMs,
      bytesDone: event.bytesDone,
      bytesTotal: event.bytesTotal,
      bitrate: event.bitrate,
      fps: event.fps,
      detail: event.detail,
    });

    this.executionService.applyExecutionProgress(event.blockId, {
      stepId: event.stepId,
      stepName: event.stepName,
      executionId: event.executionId,
      percent: event.percent,
      currentTimeMs: event.currentTimeMs,
      totalTimeMs: event.totalTimeMs,
      timeRemainingMs: event.timeRemainingMs,
      bytesDone: event.bytesDone,
      bytesTotal: event.bytesTotal,
      bitrate: event.bitrate,
      fps: event.fps,
    });
  };

  /**
   * Write lastRunAt and lastRunStatus to the Y.js block model.
   * Single write point for all execution completions (manual, test, scheduled).
   */
  private updateBlockRunStatus(
    docId: string,
    blockId: string,
    status: 'completed' | 'failed' | 'cancelled'
  ): void {
    try {
      const { doc, release } = this.docsService.open(docId);
      try {
        const blockSuiteDoc = doc.blockSuiteDoc;
        if (!blockSuiteDoc) {
          console.warn('[AutomationSocket] No blockSuiteDoc for doc:', docId);
          return;
        }

        const block = blockSuiteDoc.getBlock(blockId);
        if (!block) {
          console.warn('[AutomationSocket] Block not found:', blockId);
          return;
        }

        blockSuiteDoc.transact(() => {
          const props = block.model.props as Record<string, unknown>;
          props.lastRunAt = Date.now();
          props.lastRunStatus =
            status === 'completed'
              ? 'success'
              : status === 'cancelled'
                ? 'cancelled'
                : 'error';
        });
      } finally {
        release();
      }
    } catch (e) {
      console.error('[AutomationSocket] Failed to update block run status:', e);
    }
  }

  /**
   * Handle incoming pending decision event from backend.
   */
  private readonly handlePendingDecision = (
    event: PendingDecisionEvent
  ): void => {
    console.debug(
      '[AutomationSocket] Received pending decision event:',
      event.type,
      event.decision.id
    );

    if (!this.pendingDecisionService) {
      console.warn(
        '[AutomationSocket] PendingDecisionService not set, ignoring event'
      );
      return;
    }

    if (event.type === 'created') {
      // Add new decision to the list
      this.pendingDecisionService.addDecision(event.decision);

      // Show toast notification
      notify({
        title: 'Pending Decision',
        message: `"${event.decision.searchValue}" needs review`,
        style: 'normal',
        theme: 'warning',
      });
    } else if (event.type === 'resolved') {
      // Remove resolved decision from the list
      this.pendingDecisionService.removeDecision(event.decision.id);
    }
  };

  /**
   * sl-wqvr.7.6: Handle workflow provisioning completion event.
   * Dispatches to the provisioning hook via window callback.
   */
  private readonly handleProvisioningComplete = (
    event: WorkflowProvisioningCompleteEvent
  ): void => {
    console.debug(
      '[AutomationSocket] workflow provisioning complete for workspace:',
      event.workspaceId
    );

    // Dispatch to provisioning hook via window callback
    const key = `__workflow_provisioning_${event.workspaceId}`;
    const handler = (window as unknown as Record<string, unknown>)[key];
    if (typeof handler === 'function') {
      (handler as (e: WorkflowProvisioningCompleteEvent) => void)(event);
    }
  };

  /**
   * sl-czam: Handle workflow schema change event.
   * Dispatches to WorkflowService via window callback for schema cache invalidation.
   */
  private readonly handleSchemaChanged = (
    event: WorkflowSchemaChangedEvent
  ): void => {
    console.debug(
      '[AutomationSocket] workflow schema changed:',
      event.workflowId
    );

    const key = `__workflow_schema_changed`;
    const handler = (window as unknown as Record<string, unknown>)[key];
    if (typeof handler === 'function') {
      (handler as (e: WorkflowSchemaChangedEvent) => void)(event);
    }
  };

  private readonly handleWorkflowSnapshot = (
    event: WorkflowSnapshotEvent
  ): void => {
    this.workflowSnapshot$.next(event);
  };

  private readonly handleWorkflowWorkingCopy = (
    event: WorkflowWorkingCopyEvent
  ): void => {
    this.workflowWorkingCopy$.next(event);
  };

  /**
   * sl-9fmv.6.5: Route workflow-patch events into the PendingPatchRegistry.
   * The registry is the single source of truth for the diff-preview UI;
   * this handler is pure transport.
   */
  private readonly handleWorkflowPatch = (event: WorkflowPatchEvent): void => {
    routeWorkflowPatchEvent(this.pendingPatchRegistry, event);
  };

  override dispose(): void {
    this.disconnect();
    super.dispose();
  }
}
